import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { voiceApi } from "@/api/mist";

/**
 * Reusable Whisper dictation button. Records the mic, transcribes via the
 * platform STT endpoint, and hands the text back through `onText` so the caller
 * can append it into any text field (speak instead of type).
 */
export const MicDictate = ({ onText, disabled = false, className = "", title = "Dictate with voice", testId = "mic-dictate" }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const stop = () => recRef.current?.stop();

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) return;
        setBusy(true);
        try {
          const r = await voiceApi.transcribe(blob);
          const text = (r?.transcript || "").trim();
          if (text) { onText?.(text); toast.success("Transcribed"); }
          else toast.error("Didn't catch that — try again");
        } catch {
          toast.error("Transcription unavailable");
        } finally { setBusy(false); }
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone unavailable");
    }
  };

  const onClick = () => {
    if (busy || disabled) return;
    if (recording) stop(); else start();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={testId}
      title={recording ? "Stop & transcribe" : title}
      aria-label={recording ? "Stop recording" : "Dictate with voice"}
      className={`grid place-items-center w-8 h-8 border-2 border-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40 transition-colors ${
        recording ? "bg-[#FF5A5A] text-black animate-pulse" : busy ? "bg-[#F5C518] text-black" : "bg-[#1a1a1a] text-[#F5C518] hover:bg-[#242424]"
      } ${className}`}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={15} />}
    </button>
  );
};

export default MicDictate;
